// @ts-strict-ignore
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import {
    displayNameForFeatureAffiliation,
    colorForFeatureAffiliationTags,
    codeIsCommit,
    GetFixRequestAuthorIds,
    GetFixRequestCurrentApprovals,
} from '../../helpers';
import { iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import {
    CIBuildInfo,
    CIBuildSelectionReason,
    FeatureItem,
    IntegrationType,
    PRItem,
    ReleaseCandidateBuildInfo,
    FixRequestStatusType,
    ResolvedFeatureAffiliation,
    FixRequest,
    CommitItem,
} from '../../types';
import styles from '../styles/stepStyles';
import {
    frRowIgnoredStyle,
    antGrey,
    colorWithAlpha,
    getAvatarColorForName,
    runwayColors,
} from '../styles/globalStyles';
import CodeStatus from './CodeStatus';
import FeatureItemActions from './FeatureItemActions';
import TicketStatus from './TicketStatus';
import {
    AppstoreFilled,
    LockFilled,
    InfoCircleOutlined,
    ToolFilled,
    CheckCircleFilled,
    CloseCircleFilled,
    ClockCircleFilled,
    CheckCircleOutlined,
} from '@ant-design/icons';
import Divider from 'antd/lib/divider';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import Avatar from 'antd/lib/avatar';
import {
    CodeOwnerAvatar,
    formatCodeOwnerRowTooltip,
    formatCodeOwnerWithAICoAuthor,
    getPercentageAICoAuthoredForCodeItem,
    getPrimaryAICodeAuthorProvider,
} from './aiCoAuthorDisplayHelpers';
import { ReleaseComponentProps, StringKeyedDictionary } from '../../typescriptHelpers';
import Card from 'antd/lib/card';
import SmallStatusNode from '../SmallStatusNode';
import { statusColorForAppStoreIsProcessedStatus, statusColorForBuildStatus } from '../../helpers/displayHelpers';
import { AppBuildComponent, CIBuildComponent } from '../BuildComponents';
import LinkageIcon from '../icons/LinkageIcon';
import { useCanAddFixRequestForFeatureItem } from '../../utils/hooks/useCanAddFixRequestForFeatureItem';
import { displayUserNameLong } from '../../helpers/displayHelpers';
import IntegrationAvatar from '../IntegrationAvatar';
import { titleStyle } from '../styles/globalStyles';
import { selectOrgUser, selectRequiredFixRequestsApprovals } from '@/selectors/selectors';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { ApproverAvatar, ApproverAvatarGroup } from '../Avatars';

export const renderIcons = (
    featureManagementIntegrationType: IntegrationType,
    vcsIntegrationType: IntegrationType,
    hasTicket: boolean,
    hasCode: boolean,
    ignored: boolean,
    explanationText: string,
    divStyling?: React.CSSProperties,
    ticketIconOpacity?: number,
    appStoreFilledIconSize?: number,
    iconMargin?: number,
) => {
    const [ticketIcon, ticketAltText] =
        featureManagementIntegrationType ?
            iconAndAltForIntegrationType(featureManagementIntegrationType)
        :   [null, null];
    const [codeIcon, codeAltText] =
        vcsIntegrationType ? iconAndAltForIntegrationType(vcsIntegrationType) : [null, null];

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    return (
        <div style={ignored ? { ...frRowIgnoredStyle, ...containerStyle } : containerStyle}>
            <Tooltip title={explanationText} placement="left" mouseEnterDelay={0.4}>
                <div
                    style={
                        divStyling ? divStyling : (
                            {
                                backgroundColor: runwayColors.white,
                                borderColor: antGrey.grey5,
                                borderStyle: 'solid',
                                borderWidth: 1,
                                borderRadius: 8,
                                paddingTop: 8,
                                paddingBottom: 8,
                                paddingLeft: 5,
                                paddingRight: 5,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }
                        )
                    }
                >
                    <div
                        style={{
                            marginBottom: iconMargin ? iconMargin : 0,
                            paddingLeft: iconMargin ? iconMargin : 0,
                            paddingRight: iconMargin ? iconMargin : 0,
                        }}
                    >
                        {ticketIcon ?
                            <img
                                src={ticketIcon}
                                alt={ticketAltText}
                                width={20}
                                height={20}
                                style={{
                                    opacity:
                                        hasTicket ? 1
                                        : ticketIconOpacity ? ticketIconOpacity
                                        : 0.2,
                                }}
                            />
                        :   <AppstoreFilled
                                style={{
                                    fontSize: appStoreFilledIconSize ? appStoreFilledIconSize : 18,
                                    color: antGrey.grey6,
                                }}
                            />
                        }
                    </div>
                    <Divider style={{ margin: '10px 0px', border: `1px solid ${antGrey.grey4}`, width: 20 }} />
                    <div
                        style={{
                            marginTop: iconMargin ? iconMargin : 0,
                            paddingLeft: iconMargin ? iconMargin : 0,
                            paddingRight: iconMargin ? iconMargin : 0,
                        }}
                    >
                        {codeIcon ?
                            <img
                                src={codeIcon}
                                alt={codeAltText}
                                width={20}
                                height={20}
                                style={{ opacity: hasCode ? 1 : 0.2 }}
                            />
                        :   <AppstoreFilled
                                style={{
                                    fontSize: appStoreFilledIconSize ? appStoreFilledIconSize : 20,
                                    color: antGrey.grey6,
                                }}
                            />
                        }
                    </div>
                </div>
            </Tooltip>
        </div>
    );
};

export const renderCodeOwner = (
    code: CommitItem | PRItem | undefined | null,
    showOwnerName: boolean,
    showAICoAuthors = false,
) => {
    if (!code) {
        return null;
    }

    const codeOwner = code.author;

    if (!showAICoAuthors) {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {codeOwner && (
                    <div style={{ marginRight: 8 }}>
                        <Avatar
                            style={{
                                fontSize: 10,
                                backgroundColor: getAvatarColorForName(codeOwner),
                                flexShrink: 0,
                            }}
                            size={20}
                        >
                            {codeOwner.charAt(0).toUpperCase()}
                        </Avatar>
                    </div>
                )}
                {showOwnerName && (
                    <Paragraph
                        style={{
                            color: codeOwner ? antGrey.grey8 : antGrey.grey5,
                            marginBottom: 0,
                            lineHeight: '1.2em',
                            maxWidth: 130,
                        }}
                        ellipsis={{
                            rows: 1,
                            tooltip: {
                                title: codeOwner,
                                placement: 'right',
                            },
                        }}
                    >
                        {codeOwner ?? 'No owner'}
                    </Paragraph>
                )}
            </div>
        );
    }

    const aiProvider = getPrimaryAICodeAuthorProvider(code.aiCoAuthorProviders);
    const hasAiCoAuthors = !!code.aiCoAuthorProviders?.length;
    const percentageAICoAuthored = getPercentageAICoAuthoredForCodeItem(code);
    const ownerLabel = hasAiCoAuthors ? formatCodeOwnerWithAICoAuthor(codeOwner, code.aiCoAuthorProviders) : codeOwner;
    const rowTooltip =
        hasAiCoAuthors ?
            formatCodeOwnerRowTooltip(codeOwner, code.aiCoAuthorProviders, percentageAICoAuthored)
        :   codeOwner;
    const hasOwnerDisplay = !!(codeOwner || aiProvider);

    const ownerContent = (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {hasOwnerDisplay && (
                <div style={{ marginRight: 8 }}>
                    <CodeOwnerAvatar codeOwner={codeOwner} aiProvider={aiProvider} />
                </div>
            )}
            {showOwnerName && (
                <Paragraph
                    style={{
                        color: hasOwnerDisplay ? antGrey.grey8 : antGrey.grey5,
                        marginBottom: 0,
                        lineHeight: '1.2em',
                        maxWidth: 130,
                    }}
                    ellipsis={{
                        rows: 1,
                        tooltip: hasAiCoAuthors ? false : { title: ownerLabel, placement: 'right' },
                    }}
                >
                    {ownerLabel ?? 'No owner'}
                </Paragraph>
            )}
        </div>
    );

    if (hasAiCoAuthors) {
        return (
            <Tooltip title={rowTooltip} placement="right">
                {ownerContent}
            </Tooltip>
        );
    }

    return ownerContent;
};

export const renderItemDesc = (
    title: string,
    featureAffiliations: ResolvedFeatureAffiliation[],
    ignored: boolean,
    customFields?: StringKeyedDictionary<string[]>,
    customDescContainerStyle?: React.CSSProperties,
    customParagraphStyle?: React.CSSProperties,
) => {
    const hasFeatureAffiliationsOrCustomFields =
        featureAffiliations.length > 0 || (!!customFields && Object.keys(customFields).length > 0);
    const isfrRowIgnoredStyle = ignored ? frRowIgnoredStyle : {};
    const descContainerStyle: React.CSSProperties =
        customDescContainerStyle ? customDescContainerStyle : (
            {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                paddingRight: 12,
                minWidth: 300,
            }
        );

    return (
        <div style={descContainerStyle}>
            {title && (
                <Popover
                    content={() => {
                        return <Text>{title}</Text>;
                    }}
                    placement="topLeft"
                    overlayStyle={styles.featureItemDescriptionTooltipOverlayStyle}
                    autoAdjustOverflow={false}
                    mouseEnterDelay={2}
                >
                    <div
                        style={
                            customParagraphStyle ?
                                { ...customParagraphStyle, ...isfrRowIgnoredStyle }
                            :   { ...styles.featureItemDescriptionStyle, ...isfrRowIgnoredStyle }
                        }
                    >
                        <Paragraph
                            ellipsis={{ rows: hasFeatureAffiliationsOrCustomFields ? 1 : 2 }}
                            style={styles.featureItemParagraphStyle}
                        >
                            {title}
                        </Paragraph>
                    </div>
                </Popover>
            )}
            {hasFeatureAffiliationsOrCustomFields && (
                <Space
                    size={[8, 4]}
                    style={{
                        flexWrap: 'wrap',
                        marginTop: 8,
                    }}
                >
                    {(() => {
                        const MAX_VISIBLE_TAGS = 3;
                        const visibleAffiliations = featureAffiliations.slice(0, MAX_VISIBLE_TAGS);
                        const hiddenAffiliations = featureAffiliations.slice(MAX_VISIBLE_TAGS);
                        return (
                            <>
                                {visibleAffiliations.map((fa, i) => {
                                    return (
                                        <Tooltip
                                            key={`${fa.integrationId}-${i}`}
                                            title={displayNameForFeatureAffiliation(fa.affiliationType)}
                                        >
                                            <Tag
                                                color={colorForFeatureAffiliationTags(fa.affiliationType)}
                                                style={{
                                                    color: antGrey.grey8,
                                                    fontSize: '11px',
                                                    lineHeight: '15px',
                                                    height: '18px',
                                                }}
                                            >
                                                {fa.resolvedString}
                                            </Tag>
                                        </Tooltip>
                                    );
                                })}
                                {hiddenAffiliations.length > 0 && (
                                    <Tooltip
                                        title={
                                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                                                {hiddenAffiliations.map((fa, i) => (
                                                    <li key={`${fa.integrationId}-hidden-${i}`}>{fa.resolvedString}</li>
                                                ))}
                                            </ul>
                                        }
                                    >
                                        <Tag
                                            style={{
                                                color: antGrey.grey8,
                                                fontSize: '11px',
                                                lineHeight: '15px',
                                                height: '18px',
                                                cursor: 'default',
                                            }}
                                        >
                                            +{hiddenAffiliations.length}
                                        </Tag>
                                    </Tooltip>
                                )}
                            </>
                        );
                    })()}
                    {customFields &&
                        Object.keys(customFields).map((fieldName) => {
                            return customFields[fieldName].map((val, i) => {
                                return (
                                    <Tooltip key={`${fieldName}-${i}`} title={`${fieldName}: ${val}`}>
                                        <Tag
                                            color={antGrey.grey2}
                                            style={{
                                                color: antGrey.grey8,
                                                fontSize: '11px',
                                                lineHeight: '15px',
                                                height: '18px',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '450px',
                                                display: 'inline-block',
                                                marginTop: 8,
                                            }}
                                        >
                                            {val}
                                        </Tag>
                                    </Tooltip>
                                );
                            });
                        })}
                </Space>
            )}
        </div>
    );
};

export const renderTicketOwner = (
    hasTicket: boolean,
    ticketOwner: string,
    showOwnerName: boolean,
    customContainerStyle?: React.CSSProperties,
) => {
    const baseAvatarStyle: React.CSSProperties = {
        fontSize: 10,
    };
    const divStyle: React.CSSProperties =
        customContainerStyle ? customContainerStyle : { display: 'flex', alignItems: 'center' };

    return (
        hasTicket && (
            <div style={divStyle}>
                {ticketOwner && (
                    <Avatar
                        style={{
                            ...baseAvatarStyle,
                            backgroundColor: getAvatarColorForName(ticketOwner),
                            marginRight: 8,
                            flexShrink: 0,
                        }}
                        size={20}
                    >
                        {ticketOwner.charAt(0).toUpperCase()}
                    </Avatar>
                )}
                {showOwnerName && (
                    <Paragraph
                        style={{
                            color: ticketOwner ? antGrey.grey8 : antGrey.grey5,
                            marginBottom: 0,
                            lineHeight: '1.2em',
                            maxWidth: 130,
                        }}
                        ellipsis={{
                            rows: 1,
                            tooltip: {
                                title: ticketOwner,
                                placement: 'right',
                            },
                        }}
                    >
                        {ticketOwner ?? 'No owner'}
                    </Paragraph>
                )}
            </div>
        )
    );
};

const FixStatusTag = ({
    status,
    currentApprovals,
    requiredApprovals,
}: {
    status:
        | FixRequestStatusType.FixRequestApproved
        | FixRequestStatusType.FixRequestRejected
        | FixRequestStatusType.FixRequestPending;
    currentApprovals: number;
    requiredApprovals: number;
}) => {
    const statusIcon = {
        Approved: <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />,
        Rejected: <CloseCircleFilled style={{ color: runwayColors.statusRed }} />,
        Pending: <ClockCircleFilled style={{ color: runwayColors.statusYellow }} />,
    };

    const statusLabel = {
        Approved: `Fix Approved${requiredApprovals === 1 ? '' : ` (${currentApprovals}/${requiredApprovals})`}`,
        Rejected: 'Fix Rejected',
        Pending: `Fix Pending approval${requiredApprovals === 1 ? '' : ` (${currentApprovals}/${requiredApprovals})`}`,
    };

    return (
        <Space size="middle">
            {statusIcon[status]}
            <Text style={{ ...titleStyle, fontSize: 12, marginLeft: 2 }}>{statusLabel[status]}</Text>
        </Space>
    );
};

export interface FeatureReadinessItemProps extends FeatureItem, ReleaseComponentProps {
    latestOrSelectedStagingBuild?: ReleaseCandidateBuildInfo;
    latestWorkingBranchBuild?: CIBuildInfo;
    featureManagementIntegrationType?: IntegrationType;
    vcsIntegrationType?: IntegrationType;
    workingBranchEnabled: boolean;
    isAutoMergeRunwayPRsEnabled: boolean;
    onClickRow: () => void;
    onClickAddFixRequest: (workItemId: string) => void;
    requiresFixRequestsApprovals: boolean;
    isReleaseKickedOff: boolean;
    isReleaseSubmitted: boolean;
    shouldPreventFixRequests: boolean;
    stepRunId: string;
}

export const FeatureReadinessItem: React.FC<FeatureReadinessItemProps> = (props: FeatureReadinessItemProps) => {
    const renderBuildModule = (item: FeatureItem) => {
        let build: CIBuildInfo = null;
        let isRCBuild = false;
        if (item.isInLatestOrSelectedStagingBuild && props.latestOrSelectedStagingBuild) {
            build = props.latestOrSelectedStagingBuild;
            isRCBuild = true;
        } else if (item.isInLatestWorkingBranchBuild && props.latestWorkingBranchBuild) {
            build = props.latestWorkingBranchBuild;
        }

        const cardBodyStyle: React.CSSProperties = {
            padding: '6px 12px',
            backgroundColor: runwayColors.white,
        };

        const cardStyle: React.CSSProperties = {
            width: 130,
            overflow: 'hidden',
            border: `1.5px solid ${antGrey.grey5}`,
        };

        const isfrRowIgnoredStyle = props.ignored ? frRowIgnoredStyle : {};

        if (!build) {
            return (
                <Card
                    title={<Text style={{ color: runwayColors.darkTextGrey }}>Build</Text>}
                    style={{ ...cardStyle, ...isfrRowIgnoredStyle }}
                    headStyle={{
                        backgroundColor: antGrey.grey4,
                    }}
                    size="small"
                    bodyStyle={{ ...cardBodyStyle }}
                    className="fr-table-build"
                    bordered={false}
                >
                    <Text type="secondary">No build</Text>
                </Card>
            );
        }

        const ciBuildComponent = (
            <Space size={8} className="fr-table-build-space">
                <SmallStatusNode color={statusColorForBuildStatus(build.ciBuild.buildStatus)} size={10} />
                <CIBuildComponent
                    integrationId={build.ciBuild.integrationId}
                    buildIdentifier={build.ciBuild.buildIdentifier}
                    asLink={true}
                    imgSize={24}
                    buildUrl={build.ciBuild.buildUrl}
                />
            </Space>
        );

        let associatedAppStoreBuildComponent = null;

        if (build.associatedAppStoreBuild) {
            associatedAppStoreBuildComponent = (
                <Space size={8} className="fr-table-build-space">
                    <SmallStatusNode
                        color={statusColorForAppStoreIsProcessedStatus(build.associatedAppStoreBuild.isProcessed)}
                        size={10}
                    />
                    <AppBuildComponent
                        integrationId={build.associatedAppStoreBuild.integrationId}
                        buildIdentifier={build.associatedAppStoreBuild.buildNumber}
                        imgSize={24}
                        isProcessed={true} // force proccessing text not to show
                    />
                </Space>
            );
        }

        const rcBuild = build as ReleaseCandidateBuildInfo;
        const showLocked =
            rcBuild.isSelectedBuild &&
            rcBuild.selectedReason &&
            rcBuild.selectedReason === CIBuildSelectionReason.ManualSelection;

        return (
            <Card
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Space align="center" size={4}>
                            <Text style={{ color: runwayColors.darkTextGrey }}>{isRCBuild ? 'RC' : 'WB'}</Text>
                            {!isRCBuild && (
                                <Tooltip title="The latest working branch build that contains this item of work.">
                                    <InfoCircleOutlined style={{ color: runwayColors.primary }} />
                                </Tooltip>
                            )}
                        </Space>
                        {showLocked && <LockFilled style={{ color: antGrey.grey7 }} />}
                    </div>
                }
                size="small"
                style={{ ...cardStyle }}
                headStyle={{
                    backgroundColor: isRCBuild ? colorWithAlpha(antGrey.grey5, 0.5) : antGrey.grey4,
                }}
                bodyStyle={{ ...cardBodyStyle }}
                className="fr-table-build"
                bordered={false}
            >
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {ciBuildComponent}
                    {associatedAppStoreBuildComponent && ciBuildComponent && (
                        <Divider
                            orientation="left"
                            dashed
                            style={{
                                borderTopColor: antGrey.grey5,
                                marginTop: 0,
                                marginBottom: 0,
                                marginLeft: -3,
                            }}
                            orientationMargin={0}
                            className="fr-build-divider"
                        >
                            <LinkageIcon width={16} height={16} style={{ transform: 'rotate(90deg)' }} />
                        </Divider>
                    )}
                    {associatedAppStoreBuildComponent}
                </div>
            </Card>
        );
    };

    const canAddFixRequestForFeatureItem = useCanAddFixRequestForFeatureItem(props.id, props.stepRunId);

    const handleFeatureItemActionsClick = (event) => {
        event.stopPropagation();
    };

    const featureItemActionsOverlayMenuStyle: React.CSSProperties = {
        backgroundColor: antGrey.grey3,
        height: '100%',
        width: 'calc(100% - 12px)',
        borderRight: `12px solid ${antGrey.grey3}`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderTopRightRadius: props.fixRequest ? 0 : '8px',
        borderBottomRightRadius: '8px',
    };

    return (
        <div style={{ borderTop: `20px solid ${runwayColors.white}`, width: '100%' }}>
            {props.fixRequest && (
                <FixStatusBar
                    orgId={props.orgId}
                    appId={props.appId}
                    requiresFixRequestApprovals={props.requiresFixRequestsApprovals}
                    {...props.fixRequest}
                />
            )}
            <BaseWorkItemCard
                {...props}
                extraComponent={
                    <div
                        style={{
                            marginLeft: 12,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            paddingLeft: 12,
                        }}
                    >
                        <div
                            style={{
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginRight: 12,
                                paddingTop: 8,
                                paddingBottom: 8,
                            }}
                        >
                            {renderBuildModule(props)}
                        </div>
                        <div
                            onClick={handleFeatureItemActionsClick}
                            style={
                                props.ignored ?
                                    { ...frRowIgnoredStyle, ...featureItemActionsOverlayMenuStyle }
                                :   featureItemActionsOverlayMenuStyle
                            }
                        >
                            <div style={{ paddingLeft: 10 }}>
                                <FeatureItemActions
                                    {...props}
                                    orgId={props.orgId}
                                    appId={props.appId}
                                    stepRunId={props.stepRunId}
                                    releaseId={props.releaseId}
                                    workingBranchEnabled={props.workingBranchEnabled}
                                    requiresFixRequestsApprovals={props.requiresFixRequestsApprovals}
                                    canAddFixRequestForFeatureItem={canAddFixRequestForFeatureItem}
                                    onClickAddFixRequest={props.onClickAddFixRequest}
                                    isReleaseKickedOff={props.isReleaseKickedOff}
                                    isReleaseSubmitted={props.isReleaseSubmitted}
                                    shouldPreventFixRequests={props.shouldPreventFixRequests}
                                />
                            </div>
                        </div>
                    </div>
                }
            />
        </div>
    );
};

interface FixStatusBarProps extends FixRequest {
    orgId: string;
    appId: string;
    requiresFixRequestApprovals: boolean;
}

export const FixStatusBar: React.FC<FixStatusBarProps> = (props: FixStatusBarProps) => {
    const requesterOrgUser = useSelector((state: RootState) => selectOrgUser(state, props.orgId, props.createdBy));
    const requester = requesterOrgUser ? displayUserNameLong(requesterOrgUser) : props.createdBy;
    const requiredFixRequestsApprovals = useSelector(selectRequiredFixRequestsApprovals(props.appId)) || 1;
    const authorIds = GetFixRequestAuthorIds(props.status);
    const approverOrgUsers = useSelector(
        (state: RootState) =>
            authorIds.map((authorId) => selectOrgUser(state, props.orgId, authorId)).filter(Boolean) || [],
    );

    const detailsText =
        props.formFieldsValues
            ?.map((v) => v.value)
            .filter(Boolean)
            .join(' • ') ?? '';

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                minWidth: 0,
                backgroundColor: antGrey.grey4,
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                height: 40,
                paddingRight: 8,
                paddingLeft: 16,
                border: `1.5px solid ${antGrey.grey5}`,
                borderBottom: 'none',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    flexGrow: 1,
                    minWidth: 0,
                    gap: 12,
                }}
            >
                <div style={{ width: 240, flexShrink: 0 }}>
                    {props.requiresFixRequestApprovals ?
                        <FixStatusTag
                            status={props.status?.status ?? FixRequestStatusType.FixRequestPending}
                            currentApprovals={GetFixRequestCurrentApprovals(props.status)}
                            requiredApprovals={requiredFixRequestsApprovals}
                        />
                    :   <Space size="middle">
                            <ToolFilled style={{ color: antGrey.grey7 }} />
                            <Text
                                style={{
                                    ...titleStyle,
                                    fontSize: 12,
                                    marginLeft: 1,
                                    color: antGrey.grey8,
                                }}
                            >
                                Fix
                            </Text>
                        </Space>
                    }
                </div>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexGrow: 1,
                        marginLeft: 18,
                        minWidth: 0,
                        paddingRight: 8,
                    }}
                >
                    {/* Always show the requester badge, even when there are no fix details. */}
                    <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center', lineHeight: 0 }}>
                        {requesterOrgUser ?
                            <ApproverAvatar user={requesterOrgUser} size={16} />
                        :   <IntegrationAvatar user={requester} size={16} style={{ color: 'white', fontSize: '10px' }}>
                                {requester.charAt(0).toUpperCase()}
                            </IntegrationAvatar>
                        }
                    </span>
                    {detailsText ?
                        <Text
                            ellipsis
                            style={{
                                color: antGrey.grey8,
                                flex: 1,
                                minWidth: 0,
                                fontSize: 13,
                                lineHeight: '16px',
                                margin: 0,
                            }}
                        >
                            {detailsText}
                        </Text>
                    :   null}
                </div>
                {
                    approverOrgUsers?.length > 0 ?
                        <div
                            style={{
                                marginRight: 193,
                                width: 175,
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                lineHeight: 0,
                            }}
                        >
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 16,
                                    height: 16,
                                    flexShrink: 0,
                                }}
                            >
                                <CheckCircleOutlined
                                    style={{
                                        color: antGrey.grey6,
                                        fontSize: 16,
                                        lineHeight: '16px',
                                    }}
                                />
                            </span>

                            <span style={{ display: 'flex', alignItems: 'center', lineHeight: 0 }}>
                                <ApproverAvatarGroup approvers={approverOrgUsers} />
                            </span>
                        </div>
                        // No approvers yet: reserve roughly the owner-avatar column width so the details
                        // text truncates in line with the right end of the row's dashed separator.
                    :   <div style={{ width: 186, flexShrink: 0 }} />
                }
            </div>
            <div
                style={{
                    marginLeft: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    paddingLeft: 12,
                }}
            ></div>
        </div>
    );
};

export interface BaseWorkItemCardProps extends FeatureReadinessItemProps {
    extraComponent?: React.ReactNode;
    isAutoMergeRunwayPRsEnabled: boolean;
}

export const BaseWorkItemCard: React.FC<BaseWorkItemCardProps> = (props) => {
    const [isHovered, setIsHovered] = React.useState(false);

    const itemHasIssues = props.associatedStabilityIssues?.length > 0;

    const baseContainerStyle: React.CSSProperties = {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        paddingTop: 10,
        paddingBottom: 10,
        minWidth: 0,
    };

    const avatarContainerStyle: React.CSSProperties = {
        marginLeft: 12,
        marginRight: 12,
        width: 170,
        flexShrink: 0,
    };

    const baseContainerStyles: React.CSSProperties = {
        display: 'flex',
        alignItems: 'stretch',
        flexGrow: 1,
        minWidth: 0,
        cursor: 'pointer',
        paddingLeft: 8,
        border: `1.5px solid ${antGrey.grey5}`,
        borderTopRightRadius: props.fixRequest ? 0 : '8px',
        borderTopLeftRadius: props.fixRequest ? 0 : '8px',
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
    };

    const backgroundStyles: React.CSSProperties = isHovered ? { backgroundColor: antGrey.grey3 } : {};

    return (
        <div
            style={
                itemHasIssues ?
                    {
                        borderLeft: `2px solid ${runwayColors.statusRed}`,
                        marginTop: 4,
                        ...baseContainerStyles,
                        ...backgroundStyles,
                    }
                :   { ...baseContainerStyles, ...backgroundStyles }
            }
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={(e) => {
                e.stopPropagation();

                props.onClickRow();
            }}
        >
            <div style={props.ignored ? { ...frRowIgnoredStyle, ...baseContainerStyle } : baseContainerStyle}>
                {renderIcons(
                    props.featureManagementIntegrationType,
                    props.vcsIntegrationType,
                    !!props.ticket,
                    !!props.code || !!props.groupedCodeItems,
                    props.ignored,
                    props.explanationText,
                    {
                        paddingTop: 8,
                        paddingBottom: 8,
                        paddingLeft: 5,
                        paddingRight: 5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    },
                )}
                <div
                    style={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: 10,
                        minWidth: 0,
                    }}
                >
                    <div style={{ display: 'flex', minHeight: 60, alignItems: props.ticket ? 'flex-end' : 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                            <div style={{ width: 200, flexShrink: 0 }}>
                                <TicketStatus ticket={props.ticket} ignored={props.ignored} />
                            </div>
                            <div style={{ flexGrow: 1, marginLeft: 25, minWidth: 0 }}>
                                {renderItemDesc(
                                    props.ticket?.description,
                                    props.ticket?.featureAffiliations || [],
                                    false,
                                    props.ticket?.additionalFields,
                                )}
                            </div>
                            <div style={avatarContainerStyle}>
                                {renderTicketOwner(!!props.ticket, props.ticket?.owner, true)}
                            </div>
                        </div>
                    </div>
                    <Divider dashed style={{ margin: '10px 0px', borderColor: antGrey.grey6 }} />
                    <div style={{ display: 'flex', minHeight: 60, alignItems: props.code ? 'flex-start' : 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                            <div style={{ width: 200, flexShrink: 0 }}>
                                <CodeStatus
                                    code={props.code}
                                    appId={props.appId}
                                    ignored={props.ignored}
                                    workingBranchEnabled={props.workingBranchEnabled}
                                    isAutoMergeRunwayPRsEnabled={props.isAutoMergeRunwayPRsEnabled}
                                    automationTooltipMessage={props.automationTooltipMessage}
                                    groupedCodeItems={props.groupedCodeItems}
                                />
                            </div>
                            <div style={{ flexGrow: 1, marginLeft: 25, minWidth: 0 }}>
                                {renderItemDesc(
                                    props.code ?
                                        codeIsCommit(props.code) ? props.code.message
                                        :   (props.code as PRItem).title
                                    :   null,
                                    [],
                                    props.ignored,
                                )}
                            </div>
                            <div style={avatarContainerStyle}>{renderCodeOwner(props.code, true, true)}</div>
                        </div>
                    </div>
                </div>
            </div>
            {props.extraComponent}
        </div>
    );
};
